/**
 * Client-side payment-amount limits + validation.
 *
 * Why this exists: Razorpay does NOT expose a "max amount" API, and its
 * rejection ("amount exceeds maximum amount allowed") never states the limit.
 * So the gateway can't give the user an actionable bound. We add a sensible
 * client-side guard for instant feedback; Razorpay stays the authoritative
 * gate (the AMOUNT_TOO_LARGE catalog entry is the backstop for anything that
 * still slips through).
 *
 * `MAX_PAYMENT_LINK_AMOUNT` is a named constant on purpose — Razorpay's real
 * ceiling is account/KYC-dependent, so bump this one line if a merchant needs
 * a higher cap. It is a numeric sanity bound (in the selected currency's whole
 * units), not a currency-specific value.
 */

/** Upper sanity bound for a single payment link / request amount. */
export const MAX_PAYMENT_LINK_AMOUNT = 500_000;

export type PaymentAmountResult =
  | { ok: true; value: number }
  | { ok: false; error: string };

/**
 * Validate a user-entered payment amount. Pure + browserless (unit-tested).
 * Returns the parsed number on success, or a short, actionable error string.
 *
 * @param raw the raw field value (string from an <input>, or a number)
 * @param max override the cap (defaults to MAX_PAYMENT_LINK_AMOUNT)
 */
export function validatePaymentAmount(
  raw: string | number | null | undefined,
  max: number = MAX_PAYMENT_LINK_AMOUNT,
): PaymentAmountResult {
  if (raw === null || raw === undefined || (typeof raw === "string" && raw.trim() === "")) {
    return { ok: false, error: "Please enter an amount." };
  }

  const str = String(raw).trim();
  const value = Number(str);

  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, error: "Please enter a valid amount greater than 0." };
  }

  // Reject more than 2 decimal places (paise precision).
  const decimals = str.includes(".") ? str.split(".")[1].length : 0;
  if (decimals > 2) {
    return { ok: false, error: "Amount can have at most 2 decimal places (e.g. 499.00)." };
  }

  if (value > max) {
    return {
      ok: false,
      error: `Amount can't exceed ${max.toLocaleString("en-IN")}. Enter a smaller amount.`,
    };
  }

  return { ok: true, value };
}
